/* jshint esversion: 6 */
/* jshint esversion: 8 */
/* jshint esversion: 11 */
/* jshint ignore:start */
import { useContext, useEffect, useRef, useState } from "react";
import { MyContext } from "../../Context/MyProvider";
import { socket } from "../../socket";
import { FiMicOff, FiMic } from "react-icons/fi";
import { FaVideo, FaVideoSlash } from "react-icons/fa";
import { IoCall } from "react-icons/io5";

const WebRTCCallView = ({ Data, LeaveCall, userId, micOn, setMic, cameraOn, setCamera }) => {
    const { videoCallConfig, ImgBaseUrl } = useContext(MyContext);
    const [localStream, setLocalStream] = useState(null);
    const [remoteStream, setRemoteStream] = useState(null);
    const [status, setStatus] = useState("Initializing media...");
    
    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const peerConnectionRef = useRef(null);

    const doctorId = Data?.doctor_id;

    useEffect(() => {
        let active = true;
        let localMediaStream = null;

        const initWebRTC = async () => {
            try {
                // Get local media
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: true,
                    video: true
                });
                if (!active) {
                    stream.getTracks().forEach(t => t.stop());
                    return;
                }
                localMediaStream = stream;
                setLocalStream(stream);
                if (localVideoRef.current) {
                    localVideoRef.current.srcObject = stream;
                }

                // Prepare ICE Servers
                const iceServers = [];
                if (videoCallConfig?.stun_url) {
                    iceServers.push({ urls: videoCallConfig.stun_url });
                } else {
                    iceServers.push({ urls: "stun:stun.l.google.com:19302" });
                }
                
                if (videoCallConfig?.turn_url) {
                    iceServers.push({
                        urls: videoCallConfig.turn_url,
                        username: videoCallConfig.turn_username,
                        credential: videoCallConfig.turn_credential
                    });
                }

                const pc = new RTCPeerConnection({ iceServers });
                peerConnectionRef.current = pc;

                // Add local tracks to peer connection
                stream.getTracks().forEach(track => {
                    pc.addTrack(track, stream);
                });

                // Emit local ICE candidates
                pc.onicecandidate = (event) => {
                    if (event.candidate && socket) {
                        socket.emit("ice_candidate", {
                            receiver_id: doctorId,
                            candidate: {
                                candidate: event.candidate.candidate,
                                sdpMid: event.candidate.sdpMid,
                                sdpMLineIndex: event.candidate.sdpMLineIndex
                            }
                        });
                    }
                };

                // Stream remote track
                pc.ontrack = (event) => {
                    if (event.streams && event.streams[0]) {
                        setRemoteStream(event.streams[0]);
                        if (remoteVideoRef.current) {
                            remoteVideoRef.current.srcObject = event.streams[0];
                        }
                    }
                };

                setStatus("Waiting for Doctor...");

                // Signaling Listeners
                socket.on("webrtc_offer", async (offerData) => {
                    try {
                        const sdp = offerData.offer.sdp || offerData.offer;
                        const type = offerData.offer.type || "offer";
                        await pc.setRemoteDescription(new RTCSessionDescription({ sdp, type }));
                        
                        const answer = await pc.createAnswer();
                        await pc.setLocalDescription(answer);
                        
                        socket.emit("webrtc_answer", {
                            caller_id: offerData.sender_id || doctorId,
                            answer: {
                                sdp: answer.sdp,
                                type: answer.type
                            }
                        });
                        setStatus("Connecting...");
                    } catch (e) {
                        console.error("Error setting remote offer:", e);
                    }
                });

                socket.on("webrtc_answer", async (answerData) => {
                    try {
                        const sdp = answerData.answer.sdp || answerData.answer;
                        const type = answerData.answer.type || "answer";
                        await pc.setRemoteDescription(new RTCSessionDescription({ sdp, type }));
                        setStatus("Connected");
                    } catch (e) {
                        console.error("Error setting remote answer:", e);
                    }
                });

                socket.on("ice_candidate", async (candidateData) => {
                    try {
                        if (candidateData.candidate) {
                            const c = candidateData.candidate;
                            await pc.addIceCandidate(new RTCIceCandidate({
                                candidate: c.candidate,
                                sdpMid: c.sdpMid,
                                sdpMLineIndex: c.sdpMLineIndex
                            }));
                        }
                    } catch (e) {
                        console.error("Error adding ice candidate:", e);
                    }
                });

            } catch (err) {
                console.error("Failed to initialize WebRTC:", err);
                setStatus("Access to camera/mic denied or unavailable");
            }
        };

        initWebRTC();

        return () => {
            active = false;
            socket.off("webrtc_offer");
            socket.off("webrtc_answer");
            socket.off("ice_candidate");
            if (peerConnectionRef.current) {
                peerConnectionRef.current.close();
            }
            if (localMediaStream) {
                localMediaStream.getTracks().forEach(track => track.stop());
            }
        };
    }, [doctorId]);

    useEffect(() => {
        if (localStream) {
            localStream.getAudioTracks().forEach(track => {
                track.enabled = micOn;
            });
        }
    }, [micOn, localStream]);

    useEffect(() => {
        if (localStream) {
            localStream.getVideoTracks().forEach(track => {
                track.enabled = cameraOn;
            });
        }
    }, [cameraOn, localStream]);

    return (
        <div className="flex flex-col h-[100%] justify-between relative bg-slate-955 rounded-xl overflow-hidden shadow-2xl border border-slate-800">
            <div className="flex-1 relative w-full h-[380px] bg-slate-900">
                {remoteStream ? (
                    <div className="w-full h-full relative">
                        <video
                            ref={remoteVideoRef}
                            autoPlay
                            playsInline
                            className="w-full h-full object-cover"
                        />

                        <div className="absolute top-4 left-4 w-[100px] h-[130px] z-20 rounded-lg overflow-hidden border border-slate-700/80 shadow-2xl bg-slate-950">
                            <video
                                ref={localVideoRef}
                                autoPlay
                                playsInline
                                muted
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/90 text-white font-medium text-sm p-4 text-center z-0">
                        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-indigo-500 shadow-lg animate-pulse mb-3">
                            <img src={ImgBaseUrl + Data?.logo} alt={Data?.name} className="w-full h-full object-cover" />
                        </div>
                        <span className="text-slate-200 font-semibold tracking-wide mb-1">{Data?.name}</span>
                        <span className="text-slate-400 text-xs animate-pulse mb-4">{status}</span>
                        
                        <div className="w-[100px] h-[130px] rounded-lg overflow-hidden border border-slate-700/80 bg-slate-950 shadow-md">
                            <video
                                ref={localVideoRef}
                                autoPlay
                                playsInline
                                muted
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                )}
            </div>
            
            <div className="absolute bottom-5 left-0 right-0 flex justify-center gap-4 z-30 px-4">
                <button 
                    className={`p-3.5 rounded-full transition-all duration-300 shadow-xl border ${micOn ? 'bg-slate-800/80 hover:bg-slate-700/90 text-white border-slate-750' : 'bg-red-500/95 hover:bg-red-650 text-white border-transparent'}`} 
                    onClick={() => setMic(a => !a)}
                >
                    {micOn ? <FiMic size={18} /> : <FiMicOff size={18} />}
                </button>
                <button 
                    className={`p-3.5 rounded-full transition-all duration-300 shadow-xl border ${cameraOn ? 'bg-slate-800/80 hover:bg-slate-700/90 text-white border-slate-750' : 'bg-red-500/95 hover:bg-red-650 text-white border-transparent'}`} 
                    onClick={() => setCamera(a => !a)}
                >
                    {cameraOn ? <FaVideo size={18} /> : <FaVideoSlash size={18} />}
                </button>
                <button 
                    className="p-3.5 bg-red-600 hover:bg-red-500 rounded-full text-white transition-all duration-300 shadow-xl border border-transparent hover:scale-105" 
                    onClick={LeaveCall}
                >
                    <IoCall size={18} />
                </button>
            </div>
        </div>
    );
};

export default WebRTCCallView;
/* jshint ignore:end */
